// Creator statistics and top creators utilities
import { supabase } from '../lib/supabase.js';

/**
 * Get top creators by sales count
 * If fewer than limit creators with sales exist, fills remaining slots with all creators
 * @param {number} limit - Number of creators to fetch
 * @returns {Promise<Array>} - Array of top creators
 */
export const getTopCreatorsByVolume = async (limit = 10) => {
  try {
    console.log(`📊 Fetching top ${limit} creators by sales count...`);
    
    // First, try to get creators with sales
    const { data: creatorsWithSales, error: salesError } = await supabase
      .from('creators')
      .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created, total_volume, sales_count, created_at')
      .gt('sales_count', 0)
      .order('sales_count', { ascending: false })
      .order('total_volume', { ascending: false })
      .limit(limit);
    
    if (salesError) {
      console.error('❌ Error fetching top creators by sales:', salesError);
      throw salesError;
    }
    
    // If we got enough creators with sales, return them
    if (creatorsWithSales && creatorsWithSales.length >= limit) {
      console.log(`✅ Fetched ${creatorsWithSales.length} top creators with sales`);
      return creatorsWithSales;
    }
    
    // If we have fewer than limit creators with sales, fill remaining slots with all creators
    console.log(`⚠️ Only ${creatorsWithSales?.length || 0} creators with sales found. Fetching all creators to reach limit of ${limit}...`);
    
    const { data: allCreators, error: allError } = await supabase
      .from('creators')
      .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created, total_volume, sales_count, created_at')
      .order('sales_count', { ascending: false })
      .order('total_volume', { ascending: false })
      .order('total_pieces_created', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (allError) {
      console.error('❌ Error fetching all creators:', allError);
      throw allError;
    }
    
    console.log(`✅ Fetched ${allCreators?.length || 0} total creators (including ${creatorsWithSales?.length || 0} with sales)`);
    return allCreators || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch top creators by sales:', error);
    return [];
  }
};

/**
 * Get top creators by piece count
 * @param {number} limit - Number of creators to fetch
 * @returns {Promise<Array>} - Array of top creators
 */
export const getTopCreatorsByPieces = async (limit = 10) => {
  try {
    console.log(`📊 Fetching top ${limit} creators by pieces...`);
    
    // Query creators table directly to include verification_type
    const { data, error } = await supabase
      .from('creators')
      .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created, total_volume, created_at')
      .gt('total_pieces_created', 0)
      .order('total_pieces_created', { ascending: false })
      .order('total_volume', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('❌ Error fetching top creators by pieces:', error);
      throw error;
    }
    
    console.log('✅ Fetched top creators:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch top creators by pieces:', error);
    return [];
  }
};

/**
 * Get recently active creators
 * @param {number} limit - Number of creators to fetch
 * @returns {Promise<Array>} - Array of recently active creators
 */
export const getRecentlyActiveCreators = async (limit = 10) => {
  try {
    console.log(`📊 Fetching ${limit} recently active creators...`);
    
    // Query creators table directly to include verification_type
    const { data, error } = await supabase
      .from('creators')
      .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created, total_volume, last_active_at')
      .gt('total_pieces_created', 0)
      .order('last_active_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('❌ Error fetching recently active creators:', error);
      throw error;
    }
    
    console.log('✅ Fetched recently active creators:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch recently active creators:', error);
    return [];
  }
};

/**
 * Update creator volume after a sale or purchase
 * @param {string} walletAddress - Creator's wallet address
 * @param {number} amount - Transaction amount in SOL
 * @param {string} transactionType - 'sale' or 'purchase'
 * @returns {Promise<boolean>} - Success status
 */
export const updateCreatorVolume = async (walletAddress, amount, transactionType) => {
  try {
    console.log(`💰 Updating creator volume for ${walletAddress}: ${amount} SOL (${transactionType})`);
    
    const { error } = await supabase
      .rpc('update_creator_volume', {
        p_creator_wallet: walletAddress,
        p_amount: amount,
        p_transaction_type: transactionType
      });
    
    if (error) {
      console.error('❌ Error updating creator volume:', error);
      throw error;
    }
    
    console.log('✅ Creator volume updated successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Failed to update creator volume:', error);
    return false;
  }
};

/**
 * Log wallet activity
 * @param {string} walletAddress - Wallet address
 * @param {string} activityType - Type of activity (e.g., 'piece_created', 'piece_sold')
 * @param {Object} metadata - Additional data about the activity
 * @returns {Promise<string|null>} - Activity ID or null if failed
 */
export const logWalletActivity = async (walletAddress, activityType, metadata = {}) => {
  try {
    console.log(`📝 Logging wallet activity: ${activityType} for ${walletAddress}`);
    
    const { data, error } = await supabase
      .rpc('log_wallet_activity', {
        p_wallet: walletAddress,
        p_activity_type: activityType,
        p_metadata: metadata
      });
    
    if (error) {
      console.error('❌ Error logging wallet activity:', error);
      throw error;
    }
    
    console.log('✅ Wallet activity logged successfully:', data);
    return data;
    
  } catch (error) {
    console.error('❌ Failed to log wallet activity:', error);
    return null;
  }
};

/**
 * Get wallet activity history
 * @param {string} walletAddress - Wallet address
 * @param {number} limit - Number of activities to fetch
 * @returns {Promise<Array>} - Array of activities
 */
export const getWalletActivity = async (walletAddress, limit = 50) => {
  try {
    console.log(`📜 Fetching activity history for ${walletAddress}...`);
    
    const { data, error } = await supabase
      .from('wallet_activity')
      .select('*')
      .eq('wallet', walletAddress)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('❌ Error fetching wallet activity:', error);
      throw error;
    }
    
    console.log('✅ Fetched wallet activity:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch wallet activity:', error);
    return [];
  }
};

/**
 * Get creator statistics
 * @param {string} walletAddress - Creator's wallet address
 * @returns {Promise<Object|null>} - Creator stats or null if not found
 */
export const getCreatorStats = async (walletAddress) => {
  try {
    console.log(`📊 Fetching stats for creator: ${walletAddress}`);
    
    const { data, error } = await supabase
      .from('creator_stats')
      .select('*')
      .eq('wallet', walletAddress)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('❌ Error fetching creator stats:', error);
      throw error;
    }
    
    console.log('✅ Fetched creator stats:', data);
    return data || null;
    
  } catch (error) {
    console.error('❌ Failed to fetch creator stats:', error);
    return null;
  }
};

/**
 * Format creator data for display (convert to UI-friendly format)
 * @param {Object} creator - Creator data from database
 * @param {number} solPrice - Current SOL price in USD
 * @returns {Object} - Formatted creator data
 */
export const formatCreatorForDisplay = (creator, solPrice = 0) => {
  if (!creator) return null;
  
  const volume = parseFloat(creator.total_volume || 0);
  const volumeUSD = volume * solPrice;
  const salesCount = creator.sales_count || 0;
  
  return {
    id: creator.id,
    name: creator.handle,
    handle: `@${creator.handle}`,
    avatar: creator.profile_picture_url || null,
    pieces: `${creator.total_pieces_created} piece${creator.total_pieces_created !== 1 ? 's' : ''}`,
    piecesCount: creator.total_pieces_created,
    salesCount: salesCount,
    sales: `${salesCount} sale${salesCount !== 1 ? 's' : ''}`,
    volume: `${volume.toFixed(2)} SOL`,
    volumeSOL: volume,
    volumeUSD: volumeUSD,
    verified: creator.is_verified || false,
    verificationType: creator.verification_type || 'none',
    wallet: creator.wallet,
    bio: creator.bio,
    link: `/creator/${creator.handle}`,
    joinDate: creator.created_at
  };
};

/**
 * Get and format top creators for home page
 * @param {number} limit - Number of creators to fetch
 * @param {number} solPrice - Current SOL price in USD
 * @returns {Promise<Array>} - Array of formatted creators
 */
export const getFormattedTopCreators = async (limit = 10, solPrice = 0) => {
  try {
    // Get top creators by sales count (most successful)
    const creators = await getTopCreatorsByVolume(limit);
    
    // Format for display
    const formatted = creators.map(creator => formatCreatorForDisplay(creator, solPrice));
    
    return formatted;
    
  } catch (error) {
    console.error('❌ Failed to get formatted top creators:', error);
    return [];
  }
};

